import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, GripVertical, MoreHorizontal, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDroppable } from "@dnd-kit/core";
import { GearItemWithPacking } from "@/types/packing";
import { PackingGearItem } from "./PackingGearItem";

interface PackingCaseCardProps {
  id: string;
  name: string;
  color: string;
  items: GearItemWithPacking[];
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  onDeleteCase?: (caseId: string) => void;
  selectedItems: Set<string>;
  onItemClick: (itemId: string, event: React.MouseEvent) => void;
  isOver?: boolean;
  isCollapsed?: boolean;
  activeId?: string | null;
}

const caseIcons: Record<string, string> = {
  backpack: "🎒",
  case: "📦", 
  toolbox: "🧰",
  bag: "👜",
  box: "📋",
  default: "📦"
};

export function PackingCaseCard({
  id,
  name,
  color,
  items,
  canEdit,
  onTogglePacked,
  onDeleteCase,
  selectedItems,
  onItemClick,
  isOver = false,
  isCollapsed = false,
  activeId = null,
}: PackingCaseCardProps) {
  const [isOpen, setIsOpen] = useState(!isCollapsed);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(name);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync with parent collapsed state
  React.useEffect(() => {
    setIsOpen(!isCollapsed);
  }, [isCollapsed]);
  
  const packedCount = items.filter(item => item.is_packed).length;
  const totalCount = items.length;
  const percentage = totalCount > 0 ? Math.round((packedCount / totalCount) * 100) : 0;
  
  // Get icon based on case name
  const getIcon = () => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('backpack') || nameLower.includes('bag')) return caseIcons.backpack;
    if (nameLower.includes('toolbox') || nameLower.includes('tool')) return caseIcons.toolbox;
    if (nameLower.includes('case')) return caseIcons.case;
    return caseIcons.default;
  };

  const { setNodeRef } = useDroppable({
    id,
  });

  const handleNameEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleNameSave = () => {
    setIsEditing(false);
    // TODO: Add actual save functionality when backend supports it
  };

  const handleDeleteCase = () => {
    if (onDeleteCase) {
      onDeleteCase(id);
      setShowDeleteDialog(false);
    }
  };

  const estimatedWeight = items.reduce((total, item) => {
    // Rough weight estimation based on category
    const weights: Record<string, number> = {
      'Cameras': 2.5,
      'Power': 1.2,
      'Monitoring': 3.0,
      'Camera Support': 4.0,
      'Wireless': 0.8,
      'Audio': 1.5,
      'Storage': 0.5,
      'Lighting': 2.0,
    };
    return total + (weights[item.category] || 1.0) * item.quantity;
  }, 0);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card 
        className={`
          w-full max-w-none sm:min-w-[280px] md:min-w-[300px] flex flex-col transition-all duration-300 hover:shadow-lg hover:scale-[1.01]
          ${isOpen ? 'min-h-[200px] sm:min-h-[250px] md:min-h-[280px]' : 'min-h-fit'}
          backdrop-blur-sm border-white/5
          ${!isOpen && isOver ? 'ring-1 ring-primary/50 scale-[1.02] shadow-xl' : ''}
        `}
        style={{
          background: `rgba(255, 255, 255, 0.02)`,
          borderColor: (!isOpen && isOver) ? 'hsl(var(--primary))' : 'rgba(255, 255, 255, 0.05)',
        }}
      >
        <CardHeader className={`${isOpen ? 'pb-2 p-2 sm:p-3 md:p-4' : 'pb-1 p-2 sm:p-3 md:p-3'}`}>
          <div 
            ref={!isOpen ? setNodeRef : undefined}
            className="flex items-center justify-between"
          >
            <CollapsibleTrigger className="flex items-center gap-2 flex-1 text-left min-w-0">
              <span className="text-base sm:text-lg md:text-xl flex-shrink-0">{getIcon()}</span>
              
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <input
                    ref={inputRef}
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onBlur={handleNameSave}
                    onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
                    className="text-xs sm:text-sm md:text-base font-bold bg-transparent border-b border-primary focus:outline-none w-full"
                  />
                ) : (
                  <h3 
                    className="text-xs sm:text-sm md:text-base font-bold cursor-pointer hover:text-primary transition-colors truncate"
                    onClick={handleNameEdit}
                  >
                    {name}
                  </h3>
                )}
                
                {totalCount > 0 && (
                  <div className="text-xs text-muted-foreground mt-0.5 sm:mt-1">
                    {totalCount} items
                  </div>
                )}
              </div>
              
              {isOpen ? (
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
            
            {canEdit && (
              <div className="flex items-center gap-1 ml-1 sm:ml-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 sm:h-6 sm:w-6 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                >
                  <Trash2 className="h-2 w-2 sm:h-3 sm:w-3" />
                </Button>
                <GripVertical className="h-2 w-2 sm:h-3 sm:w-3 text-muted-foreground/50 cursor-grab hover:text-muted-foreground transition-colors" />
              </div>
            )}
          </div>
          
          {totalCount > 0 && percentage > 0 && (
            <div className="mt-1 sm:mt-2">
              <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full transition-all duration-300 rounded-full"
                  style={{ 
                    width: `${percentage}%`,
                    backgroundColor: percentage === 100 ? 'hsl(var(--primary))' : percentage > 50 ? '#eab308' : '#ef4444'
                  }}
                />
              </div>
            </div>
          )}
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="flex-1 pt-0 p-2 sm:p-3 md:p-4">
            <div 
              className={`
                min-h-[100px] sm:min-h-[120px] md:min-h-[140px] rounded-lg border border-dashed p-2 sm:p-3 transition-all duration-300
                ${isOver && isOpen ? 'border-primary/50 bg-primary/5 shadow-inner animate-pulse ring-1 ring-primary/30' : 'border-white/10'}
              `}
            >
              {items.length === 0 ? (
                <div 
                  ref={isOpen ? setNodeRef : undefined}
                  className="flex flex-col items-center justify-center h-full text-muted-foreground"
                >
                  <span className="text-xl sm:text-2xl md:text-4xl mb-1 sm:mb-2 md:mb-3 opacity-40">{getIcon()}</span>
                  <p className="text-xs sm:text-sm font-medium">Drop items here</p>
                  <p className="text-xs opacity-75 hidden sm:block text-center">Drag gear from the list below</p>
                </div>
              ) : (
                <div 
                  ref={isOpen ? setNodeRef : undefined}
                  className="space-y-1 sm:space-y-2 md:space-y-3"
                >
                  {items.map((item) => (
                    <PackingGearItem
                      key={item.id}
                      item={item}
                      canEdit={canEdit}
                      onTogglePacked={onTogglePacked}
                      isSelected={selectedItems.has(item.id)}
                      onItemClick={onItemClick}
                      isBeingDragged={activeId === item.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
      
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Case</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{name}"? {items.length > 0 && `The ${items.length} item(s) in this case will be moved back to the gear list.`} This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCase} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete Case
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Collapsible>
  );
}